import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Popover } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ControlAction } from '../../controlCenterMeta';
import {
    refusalCopyFor,
    type ActionRefusal,
} from '../../controlCenterSurfaces';

type ActionRefusalPopoverProps = {
    action: ControlAction;
    refusal: ActionRefusal;
    /**
     * `'wrap'` puts a `Popover.Trigger` around the child — for a card, which is
     * a plain `div` and cannot be a trigger by itself. `'child'` when the child
     * is already a HeroUI `Button`, which consumes the trigger context on its
     * own; wrapping one would nest a button inside `Popover.Trigger`'s own
     * `role="button"`.
     */
    trigger: 'wrap' | 'child';
    /** Layout only — the trigger sits inside the row's flex chain. */
    className?: string;
    /** Exactly one element: react-aria's `Pressable` does `Children.only`. */
    children: React.ReactNode;
};

/**
 * Why this session cannot run the action it just pressed.
 *
 * **The action is still listed.** Hiding it would make the catalog a different
 * map of the product for every role, and "28 available" a number that means
 * something different depending on who is reading. So the row stays pressable,
 * and the refusal is anchored to the thing that was pressed rather than
 * announced somewhere else on the page.
 *
 * The two kinds read differently, exactly as the salaries register's own notice
 * distinguishes them: a PIN session has a remedy it owns — sign in with a
 * password — and a caller of the wrong role does not, and is better told
 * plainly than invited to retry. Which audience the refusal names comes from
 * the action's gate, since payroll and the management surfaces do not admit the
 * same people.
 *
 * **The trigger is the press.** A refused card carries no `onClick`: it is a
 * `div`, and `ItemCard` is not built on `usePress`, so a popover wrapped around
 * one without `Popover.Trigger` would never open. `Popover.Trigger` supplies
 * `role="button"`, the tab stop, and the `aria-expanded` / `aria-controls`
 * pair, and the press reaches the page as this component asking to open.
 */
export const ActionRefusalPopover: React.FC<ActionRefusalPopoverProps> = ({
    action,
    refusal,
    trigger,
    className,
    children,
}) => {
    const { t } = useTranslation('pages');
    const copy = refusalCopyFor(action.id, refusal);
    const label = t(`controlCenter.actions.${action.id}`);

    return (
        <Popover>
            {trigger === 'child' ? (
                children
            ) : (
                <Popover.Trigger className={className}>
                    {children}
                </Popover.Trigger>
            )}
            <Popover.Content className="max-w-72" placement="bottom start">
                <Popover.Dialog
                    {...getNodeTestIdProp(
                        `control-center-refusal-${action.id}`,
                    )}
                >
                    <Popover.Arrow />
                    <Popover.Heading className="flex items-center gap-2">
                        <Icon
                            aria-hidden
                            className="size-4 text-muted"
                            icon="gravity-ui:lock"
                        />
                        {t(copy.titleKey)}
                    </Popover.Heading>
                    <p className="mt-2 text-sm text-muted">
                        {t(copy.bodyKey, { action: label })}
                    </p>
                </Popover.Dialog>
            </Popover.Content>
        </Popover>
    );
};
